import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { API, useAuth, formatApiError } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { INVITE } from '@/constants/testIds';

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invites/${token}`);
        setInvite(data);
        setName(data.name || '');
      } catch (err) {
        setLoadError(formatApiError(err?.response?.data?.detail) || 'This invitation link is not valid.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/invites/${token}/accept`,
        { password, name },
        { withCredentials: true }
      );
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || 'Could not accept invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={INVITE.page} className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(10,37,64,0.72) 0%, rgba(10,37,64,0.88) 100%), url(https://images.unsplash.com/photo-1512403754473-27835f7b9984?auto=format&fit=crop&w=1600&q=80)',
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between text-white">
          <Logo variant="wordmark" className="w-full h-auto block" alt="STEELPEAK" />
          <div className="p-14 pt-0">
            <h2 className="font-display text-4xl xl:text-5xl leading-tight max-w-md">
              You've been invited.
            </h2>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
              Set a password to activate your STEELPEAK knowledge account.
            </p>
            <div className="mt-10 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/50">
              <div className="h-px w-8 bg-[#D4AF37]" />
              <span>STPK RAG</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:px-16 bg-[#FAFAFA]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Logo variant="wordmark" className="h-7 w-auto" alt="STEELPEAK" />
          </div>

          {loading && (
            <div className="flex items-center gap-3 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying your invitation…
            </div>
          )}

          {!loading && loadError && (
            <div className="space-y-6">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-red-200 bg-red-50">
                <AlertTriangle className="h-6 w-6 text-red-600" strokeWidth={1.5} />
              </div>
              <h1 className="font-display text-4xl text-[#0A2540]">Invitation problem</h1>
              <p data-testid={INVITE.error} className="text-sm text-slate-500 leading-relaxed">
                {loadError}
              </p>
              <Link to="/login">
                <Button className="bg-[#0A2540] hover:bg-slate-800 text-white h-11 rounded-md px-5">
                  Go to sign in
                </Button>
              </Link>
            </div>
          )}

          {!loading && invite && (
            <>
              <div className="mb-10">
                <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
                  Welcome
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0A2540] leading-tight">
                  Set your password
                </h1>
                <p className="mt-3 text-sm text-slate-500">
                  <span className="text-[#0A2540] font-medium">{invite.invited_by_email}</span>{' '}
                  invited you to STEELPEAK as{' '}
                  <span className="text-[#0A2540] font-medium">{invite.email}</span>.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">
                    Full name
                  </Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid={INVITE.nameInput}
                    className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    placeholder="Jane Portfolio"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">
                    Password
                  </Label>
                  <Input
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={INVITE.passwordInput}
                    className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    placeholder="Minimum 6 characters"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">
                    Confirm password
                  </Label>
                  <Input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="h-11 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
                    placeholder="Retype password"
                  />
                </div>

                {error && (
                  <div
                    data-testid={INVITE.error}
                    className="text-sm text-red-600 border-l-2 border-red-500 pl-3 py-1"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid={INVITE.submitBtn}
                  className="w-full h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md tracking-wide gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {submitting ? 'Activating…' : 'Activate my account'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
